import { useEffect, useRef, useState, type ReactNode } from "react";
import { Ban } from "lucide-react";
import {
  resolveActionIcon,
  resolveActionIdFromLabel,
} from "@/shared/lib/actionIcons";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";
import { Button, type ButtonVariant } from "@/shared/ui/core/Button";
import { ConfirmationDialog } from "@/shared/ui/core/ConfirmationDialog";
import { Drawer } from "@/shared/ui/core/Drawer";
import {
  Modal,
  type ModalHeaderTone,
  type ModalHeight,
  type ModalIconTone,
  type ModalIconVariant,
  type ModalSize,
} from "@/shared/ui/core/Modal";
import {
  ENTITY_FORM_MODAL_SIZE,
  resolveEntityFormDismiss,
} from "@/shared/ui/core/entityFormModalPolicy";

export type EntityFormMode = "create" | "edit" | "status" | "neutral";

export type EntityFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Optional icon displayed before the modal title (kích thước = icon X: size-4). */
  icon?: ReactNode;
  /** Presentation style: 'badge' (soft-tinted rounded box matching close X button) or 'inline' (naked glyph). Default 'badge'. */
  iconVariant?: ModalIconVariant;
  /** Color theme for icon badge/glyph. When omitted, automatically matches mode ('primary' for create, 'warning' for edit/status). */
  iconTone?: ModalIconTone;
  /** Custom classes for title icon (e.g. tone colors). */
  iconClassName?: string;
  /** Form mode: 'create' for new entities (green header), 'edit' / 'status' (amber/warm orange header), or 'neutral'. If omitted, auto-detected from title and primaryLabel. */
  mode?: EntityFormMode;
  /** Optional explicit header tone override. */
  headerTone?: ModalHeaderTone;
  /** Custom classes for modal header bar. */
  headerClassName?: string;
  /** Entity id / code only — not training copy. */
  description?: string;
  size?: ModalSize;
  customWidth?: string;
  /** Viewport height budget — forwarded to Modal (`tall` for wizards). */
  height?: ModalHeight;
  /** `sheet` = MOB full-height bottom Drawer. Default WEB dialog. */
  variant?: "dialog" | "sheet";
  dirty: boolean;
  saving?: boolean;
  children: ReactNode;
  primaryLabel: string;
  cancelLabel?: string;
  /** `danger` = nút Hủy đỏ của Button. Mặc định secondary/ghost. */
  cancelVariant?: Extract<ButtonVariant, "secondary" | "ghost" | "danger">;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  /**
   * TestUI footer: "Ctrl + Enter để hoàn tất" + ghost Hủy bỏ.
   * Ctrl+Enter (và Enter ngoài textarea) gọi `onPrimary`.
   */
  showSubmitHint?: boolean;
  /** Optional extra footer action (e.g. Lưu nháp next to Xác nhận). */
  secondaryLabel?: string;
  secondaryDisabled?: boolean;
  onSecondary?: () => void;
  unsavedTitle?: string;
  unsavedDescription?: string;
  unsavedConfirmLabel?: string;
  /** Labeled overlay while `saving`. */
  loadingLabel?: string;
};

function labelIcon(label: string) {
  const id = resolveActionIdFromLabel(label);
  return id ? resolveActionIcon(id, undefined, "size-4") : null;
}

function labelIconSide(label: string): "left" | "right" {
  return resolveActionIdFromLabel(label) === "next" ? "right" : "left";
}

function extractString(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(extractString).join(" ");
  if (typeof node === "object" && "props" in node && node.props) {
    const { children } = node.props as { children?: ReactNode };
    return extractString(children);
  }
  return "";
}

function resolveEntityFormMode(
  mode: EntityFormMode | undefined,
  title: ReactNode,
  primaryLabel?: string,
): EntityFormMode {
  if (mode) return mode;

  const textToScan = `${extractString(title)} ${primaryLabel ?? ""}`.toLowerCase();

  // Check status keywords first (e.g. "Cập nhật trạng thái", "Đổi trạng thái", "Kích hoạt", "Ngưng")
  if (
    textToScan.includes("trạng thái") ||
    textToScan.includes("status") ||
    textToScan.includes("kích hoạt") ||
    textToScan.includes("ngưng hoạt động") ||
    textToScan.includes("bật/tắt")
  ) {
    return "status";
  }

  // Check edit keywords: "sửa", "cập nhật", "chỉnh sửa", "update", "edit"
  if (
    textToScan.includes("sửa") ||
    textToScan.includes("cập nhật") ||
    textToScan.includes("chỉnh sửa") ||
    /\b(edit|update)\b/i.test(textToScan)
  ) {
    return "edit";
  }

  // Check create keywords: "tạo", "thêm", "create", "new", "add"
  if (
    textToScan.includes("tạo") ||
    textToScan.includes("thêm") ||
    /\b(create|new|add)\b/i.test(textToScan)
  ) {
    return "create";
  }

  return "create";
}

/** MX4: compact/standard (sm|md|lg) → bottom sheet on phone; lines xl/2xl stay centered. */
function useOpsPhoneSheet(
  variant: "dialog" | "sheet",
  size: ModalSize,
): boolean {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  if (variant === "sheet") return true;
  if (isDesktop) return false;
  return (
    size === "sm" ||
    size === "small" ||
    size === "md" ||
    size === "medium" ||
    size === "lg" ||
    size === "large"
  );
}

/**
 * Create/edit chrome: WEB Modal or MOB full-height sheet + unsaved guard.
 * Feature screens supply fields and call services; this shell has no domain rules.
 */
export function EntityFormModal({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = "badge",
  iconTone,
  iconClassName,
  mode,
  headerTone,
  headerClassName,
  description,
  size = ENTITY_FORM_MODAL_SIZE.standard,
  customWidth,
  height = "default",
  variant = "dialog",
  dirty,
  saving = false,
  children,
  primaryLabel,
  cancelLabel = "Hủy",
  cancelVariant,
  primaryDisabled = false,
  onPrimary,
  showSubmitHint = false,
  secondaryLabel,
  secondaryDisabled = false,
  onSecondary,
  unsavedTitle = "Hủy thay đổi?",
  unsavedDescription = "Thông tin chưa lưu sẽ bị mất.",
  unsavedConfirmLabel = "Hủy thay đổi",
  loadingLabel = "Đang lưu...",
}: EntityFormModalProps) {
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const initialDirtyRef = useRef(dirty);
  const sheet = useOpsPhoneSheet(variant, size);
  const btnSize = sheet ? "touch" : "md";
  const defaultTitleIcon = labelIcon(primaryLabel);
  const effectiveTitleIcon =
    icon !== undefined
      ? icon
      : typeof title === "string"
        ? defaultTitleIcon
        : undefined;

  const resolvedMode = resolveEntityFormMode(mode, title, primaryLabel);
  const effectiveHeaderTone: ModalHeaderTone =
    headerTone ??
    (resolvedMode === "edit"
      ? "edit"
      : resolvedMode === "status"
        ? "status"
        : resolvedMode === "neutral"
          ? "neutral"
          : "create");
  const effectiveIconTone: ModalIconTone =
    iconTone !== undefined
      ? iconTone
      : (resolvedMode === "edit" || resolvedMode === "status")
        ? "warning"
        : resolvedMode === "neutral"
          ? "neutral"
          : "primary";

  useEffect(() => {
    if (open) {
      initialDirtyRef.current = dirty;
      setHasUserEdited(false);
    } else {
      setUnsavedOpen(false);
      setHasUserEdited(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && initialDirtyRef.current === false && dirty === true) {
      setHasUserEdited(true);
    }
  }, [open, dirty]);

  const markUserEdited = () => {
    if (!hasUserEdited) {
      setHasUserEdited(true);
    }
  };

  useEffect(() => {
    if (!open || !showSubmitHint) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || saving || unsavedOpen || primaryDisabled) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("button")) return;
      const inTextarea = target?.tagName === "TEXTAREA";
      const combo = e.ctrlKey || e.metaKey;
      if (!combo && inTextarea) return;
      e.preventDefault();
      onPrimary();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, showSubmitHint, saving, unsavedOpen, primaryDisabled, onPrimary]);

  const requestClose = () => {
    // Nếu form không dirty, đóng ngay lập tức không cần confirm
    if (!dirty) {
      onOpenChange(false);
      return;
    }

    // Trường hợp modal truyền dirty={open} từ đầu: nếu user chưa thao tác/chỉnh sửa gì thì đóng luôn
    if (initialDirtyRef.current === true && !hasUserEdited) {
      onOpenChange(false);
      return;
    }

    const intent = resolveEntityFormDismiss({ dirty, saving });
    if (intent === "block") return;
    if (intent === "unsaved") {
      setUnsavedOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const onShellOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  const effectiveCancelIcon =
    labelIcon(cancelLabel) ?? <Ban className="size-4" aria-hidden />;

  const actions = (
    <>
      <Button
        variant="secondary"
        size={btnSize}
        fullWidth={sheet}
        disabled={saving}
        leftIcon={effectiveCancelIcon}
        onClick={requestClose}
      >
        {cancelLabel}
      </Button>
      {onSecondary && secondaryLabel ? (
        <Button
          variant="secondary"
          size={btnSize}
          fullWidth={sheet}
          disabled={saving || secondaryDisabled}
          leftIcon={labelIconSide(secondaryLabel) === "left" ? labelIcon(secondaryLabel) : undefined}
          rightIcon={labelIconSide(secondaryLabel) === "right" ? labelIcon(secondaryLabel) : undefined}
          onClick={onSecondary}
        >
          {secondaryLabel}
        </Button>
      ) : null}
      <Button
        size={btnSize}
        fullWidth={sheet}
        loading={saving}
        disabled={primaryDisabled}
        leftIcon={labelIconSide(primaryLabel) === "left" ? labelIcon(primaryLabel) : undefined}
        rightIcon={labelIconSide(primaryLabel) === "right" ? labelIcon(primaryLabel) : undefined}
        onClick={onPrimary}
      >
        {primaryLabel}
      </Button>
    </>
  );

  const footer = showSubmitHint ? (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="hidden items-center gap-1.5 text-caption text-text-muted sm:flex">
        <span className="rounded border border-border bg-bg px-1.5 py-0.5 font-mono text-[10px]">
          Enter
        </span>
        <span>để hoàn tất</span>
      </div>
      <div
        className={
          sheet
            ? "flex w-full flex-col-reverse gap-2"
            : "ml-auto flex items-center gap-2"
        }
      >
        {actions}
      </div>
    </div>
  ) : (
    actions
  );

  return (
    <>
      {sheet ? (
        <Drawer
          open={open}
          onOpenChange={onShellOpenChange}
          title={title}
          icon={effectiveTitleIcon}
          iconVariant={iconVariant}
          iconTone={effectiveIconTone}
          iconClassName={iconClassName}
          headerTone={effectiveHeaderTone}
          headerClassName={headerClassName}
          description={description}
          placement="bottom"
          fullHeight
          preventOutsideClose
          dismissible={!saving}
          loading={saving}
          loadingLabel={loadingLabel}
          footer={footer}
        >
          <div
            onInputCapture={markUserEdited}
            onChangeCapture={markUserEdited}
            onKeyDownCapture={markUserEdited}
            onPointerDownCapture={markUserEdited}
          >
            {children}
          </div>
        </Drawer>
      ) : (
        <Modal
          open={open}
          onOpenChange={onShellOpenChange}
          title={title}
          icon={effectiveTitleIcon}
          iconVariant={iconVariant}
          iconTone={effectiveIconTone}
          iconClassName={iconClassName}
          headerTone={effectiveHeaderTone}
          headerClassName={headerClassName}
          description={description}
          size={size}
          customWidth={customWidth}
          height={height}
          preventOutsideClose
          dismissible={!saving}
          loading={saving}
          loadingLabel={loadingLabel}
          footer={footer}
        >
          <div
            onInputCapture={markUserEdited}
            onChangeCapture={markUserEdited}
            onKeyDownCapture={markUserEdited}
            onPointerDownCapture={markUserEdited}
          >
            {children}
          </div>
        </Modal>
      )}
      <ConfirmationDialog
        open={unsavedOpen}
        onOpenChange={setUnsavedOpen}
        tone="unsaved"
        title={unsavedTitle}
        description={unsavedDescription}
        confirmLabel={unsavedConfirmLabel}
        cancelLabel="Tiếp tục sửa"
        onConfirm={() => {
          setUnsavedOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
